"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { ApiWave } from "@/generated/models/ApiWave";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WaveContent } from "./WaveContent";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useSidebarState } from "../../../hooks/useSidebarState";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
interface BrainRightSidebarProps {
  readonly waveId: string | null | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly activeTab: SidebarTab;
  readonly setActiveTab: (tab: SidebarTab) => void;
  readonly variant?: "overlay" | "inline";
}
export enum Mode {
  CONTENT = "CONTENT",
  FOLLOWERS = "FOLLOWERS",
}
export enum SidebarTab {
  ABOUT = "ABOUT",
  LEADERBOARD = "LEADERBOARD",
  WINNERS = "WINNERS",
  TOP_VOTERS = "TOP_VOTERS",
  ACTIVITY_LOG = "ACTIVITY_LOG",
}
interface SidebarState {
  readonly wave: ApiWave | undefined;
  readonly mode: Mode;
  readonly setMode: React.Dispatch<React.SetStateAction<Mode>>;
  readonly close: () => void;
}
const useBrainRightSidebarState = waveId => {
  const $ = _c(11);
  const {
    closeRightSidebar
  } = useSidebarState();
  let t0;
  let t1;
  if ($[0] !== waveId) {
    t0 = [QueryKey.WAVE, {
      wave_id: waveId
    }];
    t1 = async () => await commonApiFetch({
      endpoint: `waves/${waveId}`
    });
    $[0] = waveId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!waveId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 60000,
      placeholderData: keepPreviousData
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: wave
  } = useQuery(t3);
  const [mode, setMode] = useState(Mode.CONTENT);
  let t4;
  if ($[7] !== closeRightSidebar || $[8] !== mode || $[9] !== wave) {
    t4 = {
      wave,
      mode,
      setMode,
      close: closeRightSidebar
    };
    $[7] = closeRightSidebar;
    $[8] = mode;
    $[9] = wave;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const SidebarContent = t0 => {
  const $ = _c(7);
  const {
    wave,
    mode,
    setMode,
    activeTab,
    setActiveTab,
    onDropClick
  } = t0;
  if (!wave) {
    return null;
  }
  let t1;
  if ($[0] !== activeTab || $[1] !== mode || $[2] !== onDropClick || $[3] !== setActiveTab || $[4] !== setMode || $[5] !== wave) {
    t1 = <WaveContent wave={wave} mode={mode} setMode={setMode} activeTab={activeTab} setActiveTab={setActiveTab} onDropClick={onDropClick} />;
    $[0] = activeTab;
    $[1] = mode;
    $[2] = onDropClick;
    $[3] = setActiveTab;
    $[4] = setMode;
    $[5] = wave;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const BrainRightSidebar: React.FC<BrainRightSidebarProps> = t0 => {
  const $ = _c(22);
  const {
    waveId,
    onDropClick,
    activeTab,
    setActiveTab,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "overlay" : t1;
  const {
    wave,
    mode,
    setMode,
    close
  } = useBrainRightSidebarState(waveId);
  const isOverlay = variant === "overlay";
  const t2 = `tw-flex tw-flex-col tw-w-[20.5rem] tw-bg-iron-950 ${isOverlay ? "tw-fixed tw-inset-y-0 tw-right-0 tw-z-[100] tw-shadow-2xl lg:tw-bg-opacity-95 min-[1300px]:tw-bg-opacity-100 lg:tw-backdrop-blur min-[1300px]:tw-backdrop-blur-none tw-border-l tw-border-solid tw-border-iron-800 tw-border-y-0 tw-border-r-0" : "tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg overflow-hidden tw-max-h-[calc(100dvh-16px)]"}`;
  let t3;
  if ($[0] !== close || $[1] !== isOverlay) {
    t3 = isOverlay && <div className="tw-absolute tw-top-2 -tw-left-5 tw-z-[110]"><button type="button" onClick={close} className="tw-group tw-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-bg-iron-700 tw-border tw-border-iron-650 tw-border-solid tw-border-r-0 tw-rounded-xl tw-shadow-[0_8px_20px_rgba(0,0,0,0.35)] tw-transition-all tw-duration-200 desktop-hover:hover:tw-bg-iron-600 desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-shadow-[0_12px_26px_rgba(0,0,0,0.45)]" aria-label="Close sidebar"><ChevronDoubleRightIcon strokeWidth={2} className="tw-h-4 tw-w-4 tw-text-iron-200 tw-transition-all tw-duration-200" /></button></div>;
    $[0] = close;
    $[1] = isOverlay;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== activeTab || $[4] !== mode || $[5] !== onDropClick || $[6] !== setActiveTab || $[7] !== setMode || $[8] !== wave) {
    t4 = <div className="tw-text-iron-500 tw-text-sm tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300 tw-h-full"><SidebarContent wave={wave} mode={mode} setMode={setMode} activeTab={activeTab} setActiveTab={setActiveTab} onDropClick={onDropClick} /></div>;
    $[3] = activeTab;
    $[4] = mode;
    $[5] = onDropClick;
    $[6] = setActiveTab;
    $[7] = setMode;
    $[8] = wave;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const panel = t5;
  if (isOverlay && typeof document !== "undefined" && document.body) {
    let t6;
    if ($[14] !== close) {
      t6 = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          close();
        }
      };
      $[14] = close;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== close || $[17] !== t6) {
      t7 = <button type="button" onClick={close} aria-label="Close right overlay" className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-[90] tw-border-0 focus:tw-outline-none" onKeyDown={t6} />;
      $[16] = close;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== panel || $[20] !== t7) {
      t8 = createPortal(<>{t7}{panel}</>, document.body);
      $[19] = panel;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  return panel;
};
export default BrainRightSidebar;